import React, { useContext, useState, useRef } from "react";

//axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../BaseUrl";

//functions
import {
  _t,
  getCookie,
  customStyle,
} from "../../../../functions/Functions";
import { useTranslation } from "react-i18next";

//3rd party packages
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Moment from "react-moment";
import moment from "moment";
import Select from "react-select";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useReactToPrint } from "react-to-print";

//pages & includes
import ReportSidebar from "./ReportSidebar";

//context consumer
import { SettingsContext } from "../../../../contexts/Settings";
import { UserContext } from "../../../../contexts/User";
import { RestaurantContext } from "../../../../contexts/Restaurant";
import CustomSpinLoader from "../../../components/CustomSpinLoader";
import ReportTypeOptions from "../../../components/Reports/ReportTypeOptions";
import ReportExcelPrintGap from "../../../components/Reports/ReportExcelPrintGap";

let groupArr = [
  { id: 1, name: "Document No" },
  { id: 2, name: "Document Date" },
  { id: 3, name: "Branch" },
  { id: 4, name: "Type" },
];

const typeOptions = [
  { label: "Raw", value: "raw" },
  { label: "Food", value: "food" },
  { label: "Property", value: "property" },
];

const WastageReport = () => {
  const { t } = useTranslation();
  // const history = useHistory();
  //getting context values here
  let {
    loading,
    dataPaginating,
    setDataPaginating,
  } = useContext(SettingsContext);
  const { authUserInfo } = useContext(UserContext);
  const { branchForSearch } = useContext(RestaurantContext);

  //all data
  const [reportData, setReportData] = useState(null);
  const componentRef = useRef();
  const componentRefThermal = useRef();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [branch, setBranch] = useState(null);
  const [type, setType] = useState(typeOptions[0]);
  const [group, setGroup] = useState(groupArr[1]);
  const [generatedReport, setGeneratedReport] = useState(false);
  const [option, setOption] = useState("excel");
  const [category, setCategory] = useState("raw");

  let common_toast = {
    position: "bottom-center",
    closeButton: false,
    autoClose: 10000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    className: "text-center toast-notification",
  };

  const handleOption = (e) => {
    setOption(e.target.value);
  };

  const thermalPrint = useReactToPrint({
    content: () => componentRefThermal.current,
    pageStyle: `
        @page {
          margin: 15px 7px;
        }
      `,
  });

  //get excel reports filter
  const getExcelReport = () => {
    setReportData(null);
    setGeneratedReport(false);
    if (category === "food") {
      if (startDate !== null && endDate !== null && branch?.id) {
        getReport();
      } else {
        if (!startDate || !endDate) {
          toast.error(
            `${_t(t("Please select from date and to date"))}`,
            common_toast
          );
        } else if (!branch?.id) {
          toast.error(
            `${_t(t("Please select branch to generate report"))}`,
            common_toast
          );
        }
      }
    } else {
      if (startDate !== null && endDate !== null && group !== null) {
        getReport();
      } else {
        toast.error(
          `${_t(t("Please select from date, to date and group"))}`,
          common_toast
        );
      }
    }
  };

  //get sale inv report
  const getReport = async () => {
    if (category === "food") {
      if (!branch?.id) {
        toast.error(`${_t(t("Please select branch to generate report"))}`, common_toast);
        return;
      }
    } else {
      if (
        (authUserInfo.details.user_type === "staff" || authUserInfo.details.user_type === "manager") &&
        !branch?.id
      ) {
        toast.error(`${_t(t("Please select branch to generate report"))}`, common_toast);
        return;
      }
    }

    let fromDate = moment(startDate).format("YYYY-MM-DD");
    let toDate = moment(endDate).format("YYYY-MM-DD");
    setDataPaginating(true);

    if (category === "food") {
      const url = BASE_URL + "/settings/get-stock-wastage-food-report";
      const payload = {
        fromDate: fromDate,
        toDate: toDate,
        branch_id: String(branch?.id),
      };

      return axios
        .post(url, payload, {
          headers: { Authorization: `Bearer ${getCookie()}` },
        })
        .then((res) => {
          setGeneratedReport(true);
          setReportData(res.data);
          setDataPaginating(false);
          if (option === "excel") {
            componentRef.current.handleDownload();
          } else {
            thermalPrint();
          }
        })
        .catch((error) => {
          setDataPaginating(false);
          toast.error(`${_t(t("Please try again"))}`, common_toast);
        });
    } else {
      const url = BASE_URL + "/settings/get-stock-wastage-report";
      let formData = new FormData();
      formData.append("fromDate", fromDate);
      formData.append("toDate", toDate);
      formData.append("type", type?.value ?? "");
      formData.append("group_by", group.name);
      formData.append("branch_id", branch?.id ?? "");

      return axios
        .post(url, formData, {
          headers: { Authorization: `Bearer ${getCookie()}` },
        })
        .then((res) => {
          setGeneratedReport(true);
          setReportData(res.data);
          setDataPaginating(false);
          if (option === "excel") {
            componentRef.current.handleDownload();
          } else {
            thermalPrint();
          }
        })
        .catch((error) => {
          setDataPaginating(false);
          toast.error(`${_t(t("Please try again"))}`, common_toast);
        });
    }
  };

  const header = [
    { name: "Group Name", key: "group_name" },
    { name: "Document No", key: "document_no", gap:1 },
    { name: "Document Id", key: "document_identity" },
    {
      name: "Date",
      render: (item) => <Moment format="LL">{item.document_date}</Moment>,
    },
    { name: "Type", key: "type" },
    { name: "Branch", key: "branch_name" },
    { name: "Item Name", key: "item_name" },
    { name: "Quantity", key: "qty" },
    { name: "Unit", key: "unit",},
    { name: "SW Document", key: "sw_document",},
    { name: "Remarks", key: "remarks" },
  ];

  const foodHeader = [
    { name: "Group", key: "group" },
    { name: "Foods Item", key: "food_item" },
    { name: "Total Qty", key: "total_qty", total: 1 },
  ];

  return (
    <>
      <Helmet>
        <title>{_t(t("Wastage Report"))}</title>
      </Helmet>
      <ReactHTMLTableToExcel
        id="test-table-xls-button"
        className="download-table-xls-button btn btn-success float-right mr-3 invisible"
        table="table-to-xls"
        filename={category === "food" ? "Stock Wastage Food Report" : "Wastage Report"}
        sheet="sheet 1"
        buttonText={category === "food" ? "Wastage Food Detail" : "Wastage Detail"}
        ref={componentRef}
      />

      {/* main body */}
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ReportSidebar />
            </div>
            {/* left Sidebar ends */}

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div
                  className="fk-scroll--pos-menu table-bottom-info-hide"
                  data-simplebar
                >
                  <div className="t-pl-15 t-pr-15">
                    {/* next page data spin loading */}
                    <CustomSpinLoader loading={loading || dataPaginating} />

                    {/* spin loading ends */}

                    <div key="smtp-form">
                      <div className="gx-2 align-items-center t-pt-15 t-pb-15">
                        <div className="row t-mb-15 mb-md-0">
                          <ul className="t-list fk-breadcrumb col-6">
                            <li className="fk-breadcrumb__list">
                              <span className="pl-3 t-link fk-breadcrumb__link text-capitalize">
                                {_t(t("Wastage Report"))}
                              </span>
                            </li>
                          </ul>
                          <div className="col-6 d-flex justify-content-end align-items-center">
                            <div className="d-flex align-items-center mr-4">
                              <div className="d-flex align-items-center mr-3">
                                <input
                                  type="radio"
                                  className="form-check-input pointer-cursor"
                                  id="report_category_raw"
                                  name="wastage_category"
                                  value="raw"
                                  onChange={(e) => {
                                    setCategory(e.target.value);
                                    setReportData(null);
                                    setGeneratedReport(false);
                                  }}
                                  checked={category === "raw"}
                                />
                                <label
                                  className="form-radio-label pointer-cursor ml-2 pt-1 mb-0"
                                  htmlFor="report_category_raw"
                                  style={{ fontSize: "16px" }}
                                >
                                  {_t(t("Raw"))}
                                </label>
                              </div>
                              <div className="d-flex align-items-center">
                                <input
                                  type="radio"
                                  className="form-check-input pointer-cursor"
                                  id="report_category_food"
                                  name="wastage_category"
                                  value="food"
                                  onChange={(e) => {
                                    setCategory(e.target.value);
                                    setReportData(null);
                                    setGeneratedReport(false);
                                  }}
                                  checked={category === "food"}
                                />
                                <label
                                  className="form-radio-label pointer-cursor ml-2 pt-1 mb-0"
                                  htmlFor="report_category_food"
                                  style={{ fontSize: "16px" }}
                                >
                                  {_t(t("Food"))}
                                </label>
                              </div>
                            </div>
                            <ReportTypeOptions
                              selectedOption={option}
                              onChange={handleOption}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="row mt-2">
                        <div className="col-2">
                          <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            peekNextMonth
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            className="form-control"
                            placeholderText={_t(t("From date"))}
                            shouldCloseOnSelect={false}
                          />
                        </div>

                        <div className="col-2">
                          <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            peekNextMonth
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            className="form-control"
                            placeholderText={_t(t("To date"))}
                            shouldCloseOnSelect={false}
                          />
                        </div>

                        <div className={category === "raw" ? "col-2" : "col-3"}>
                          <Select
                            options={branchForSearch || []}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id}
                            value={branch}
                            styles={customStyle}
                            classNamePrefix="select"
                            isClearable
                            className="table-text"
                            onChange={(branch) => setBranch(branch)}
                            placeholder={_t(t("Select Branch")) + ".."}
                          />
                        </div>
                        
                        {category === "raw" ? (
                          <>
                            <div className="col-2">
                              <Select
                                options={typeOptions}
                                value={type}
                                styles={customStyle}
                                classNamePrefix="select"
                                className="table-text"
                                onChange={setType}
                                placeholder={_t(t("Select Type")) + ".."}
                              />
                            </div>
                            <div className="col-2">
                              <Select
                                options={groupArr && groupArr}
                                getOptionLabel={(option) => option.name}
                                getOptionValue={(option) => option.id}
                                value={group}
                                styles={customStyle}
                                classNamePrefix="select"
                                className="table-text"
                                onChange={(group) => setGroup(group)}
                                maxMenuHeight="200px"
                                placeholder={_t(t("Select group")) + ".."}
                              />
                            </div>
                          </>
                        ) : (
                          <div className="col-3"></div>
                        )}
                        <div className="col-2 text-right">
                          <button
                            className="ml-1 btn btn-block btn-primary sm-text py-2 rounded"
                            onClick={(e) => getExcelReport()}
                          >
                            GENERATE REPORT
                          </button>
                        </div>
                      </div>
                      <div className="row mt-3">
                        <div className="col-5"></div>
                      </div>
                      <ReportExcelPrintGap
                        header={category === "food" ? foodHeader : header}
                        generatedReport={generatedReport}
                        heading={category === "food" ? "Stock Wastage Food Report" : "Wastage Report"}
                        reportData={reportData}
                        startDate={startDate}
                        endDate={endDate}
                        componentRef={componentRef}
                        printRef={componentRefThermal}
                        branch={{
                          name: branch?.name ?? "",
                          show: true,
                          removeTotal: category !== "food"
                        }}
                        type={option}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Rightbar contents end*/}
          </div>
        </div>
      </main>
    </>
  );
};

export default WastageReport;
